import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { EmployeeData } from "../../../hooks";
import { getInitialTraitMaxValues, getTraitGroups } from "../helpers";

import {
  useEstimateGrades,
  useGetPaygapSettings,
  useUpdatePaygapSettings,
  GradeEstimatorResponseDto,
} from "@api/hooks/paygap";
import { useGetCurrentCompanyPersonnel } from "@api";
import { useUserStore } from "@store/user";

export interface JobSeniorityCombination {
  jobTitle: string;
  seniority: string;
  key: string;
}

export interface GradeRange {
  id: number;
  start: number;
  end: number;
}

interface UseGradeEstimatorStateProps {
  employeeData: EmployeeData[];
  setEmployeeData: (data: EmployeeData[]) => void;
  handleImportData: (data: EmployeeData[]) => Promise<void>;
}

export type PositionAnswer = {
  rating?: number;
  comment?: string;
};

export type PositionAnswersMap = Record<string, PositionAnswer>;

export interface PositionListItem {
  id: string;
  jobTitle: string;
  similarJobTitles: string;
  grade: number;
}

export type GradingLevelType = "levelOfRequirements" | "levelOfRequirements2" | "levelOfRequirements3";

interface UseGradeEstimatorStateReturn {
  // State
  showGradeEstimator: boolean;
  setShowGradeEstimator: (show: boolean) => void;
  showGradeOverwriteConfirm: boolean;
  setShowGradeOverwriteConfirm: (show: boolean) => void;
  showSaveSuccess: boolean;
  traitMaxValues: Record<string, number>;
  setTraitMaxValues: React.Dispatch<React.SetStateAction<Record<string, number>>>;
  comboPoints: Record<string, Record<string, number>>;
  setComboPoints: React.Dispatch<
    React.SetStateAction<Record<string, Record<string, number>>>
  >;
  questionnaireAnswers: Record<string, PositionAnswersMap>;
  setQuestionnaireAnswers: React.Dispatch<
    React.SetStateAction<Record<string, PositionAnswersMap>>
  >;
  // Grading level selection
  selectedGradingLevel: GradingLevelType;
  setSelectedGradingLevel: React.Dispatch<React.SetStateAction<GradingLevelType>>;

  // Position list state
  positionList: PositionListItem[];
  setPositionList: React.Dispatch<React.SetStateAction<PositionListItem[]>>;
  jobTitleMatches: Record<string, string | null>;
  setJobTitleMatches: React.Dispatch<
    React.SetStateAction<Record<string, string | null>>
  >;
  matchedGrades: Record<string, number | null>;
  setMatchedGrades: React.Dispatch<
    React.SetStateAction<Record<string, number | null>>
  >;

  // Computed values
  parentTotals: Record<string, number>;
  totalMaxPoints: number;
  displayGrades: GradeEstimatorResponseDto[] | null;
  gradeRanges: GradeRange[];
  uniqueJobSeniorities: JobSeniorityCombination[];

  // Personnel data
  isLoadingGetCurrentCompanyPersonnel: boolean;

  // Model state
  isModelLoading: boolean;
  modelError: string | null;

  // Handlers
  runGradeEstimation: () => void;
  resetModelState: () => void;
  handleSaveGrades: () => void;
  handleSaveSettings: () => void;
  handleSaveQuestionnaireAnswers: (
    comboKey: string,
    newAnswers: PositionAnswersMap,
    updatedComboPoints: Record<string, Record<string, number>>,
  ) => void;
  applyGradesToEmployees: (gradingLevel: GradingLevelType) => Promise<void>;
  getGradeForPoints: (sum: number) => string;
  handleSavePositionList: (list: PositionListItem[]) => void;
  handleSaveJobTitleMatches: (
    updatedJobTitleMatches: Record<string, string | null>,
    updatedMatchedGrades: Record<string, number | null>,
  ) => void;
  handleSyncPositions: () => void;

  // Validation
  canRunEstimation: boolean;
}

export const useGradeEstimatorState = ({
  employeeData,
  setEmployeeData,
  handleImportData,
}: UseGradeEstimatorStateProps): UseGradeEstimatorStateReturn => {
  const { t } = useTranslation();
  const { user } = useUserStore();
  const {
    personnelData,
    refetchGetCurrentCompanyPersonnel,
    isLoadingGetCurrentCompanyPersonnel,
  } = useGetCurrentCompanyPersonnel(user, true);

  // API hooks for persisting grade estimator settings to database
  const { settings: paygapSettings } = useGetPaygapSettings(user, true);
  const { updateSettings: updatePaygapSettings } = useUpdatePaygapSettings(user);

  const traitGroups = useMemo(() => getTraitGroups(t), [t]);

  // State
  const [showGradeEstimator, setShowGradeEstimator] = useState(false);
  const [showGradeOverwriteConfirm, setShowGradeOverwriteConfirm] = useState(false);
  const [showSaveSuccess, setShowSaveSuccess] = useState(false);
  const [traitMaxValues, setTraitMaxValues] = useState<Record<string, number>>(() =>
    getInitialTraitMaxValues(traitGroups),
  );
  const [savedModelGrades, setSavedModelGrades] = useState<
    GradeEstimatorResponseDto[] | null
  >(null);
  const [comboPoints, setComboPoints] = useState<
    Record<string, Record<string, number>>
  >({});
  const [questionnaireAnswers, setQuestionnaireAnswers] = useState<
    Record<string, PositionAnswersMap>
  >({});
  const [positionList, setPositionList] = useState<PositionListItem[]>([]);
  const [jobTitleMatches, setJobTitleMatches] = useState<
    Record<string, string | null>
  >({});
  const [matchedGrades, setMatchedGrades] = useState<
    Record<string, number | null>
  >({});
  const [selectedGradingLevel, setSelectedGradingLevel] = useState<GradingLevelType>("levelOfRequirements");

  // Grade estimator hook (uses backend API)
  const {
    estimateGrades,
    isPendingEstimateGrades: isModelLoading,
    estimateGradesError,
    resetEstimateGrades: resetModelState,
  } = useEstimateGrades(user);

  const [modelGrades, setModelGrades] = useState<GradeEstimatorResponseDto[] | null>(
    null,
  );
  const modelError = estimateGradesError?.message || null;

  // Computed values
  const parentTotals = useMemo(() => {
    const totals: Record<string, number> = {};

    traitGroups.forEach(({ parent, children }) => {
      totals[parent.id] = children.reduce(
        (sum, child) => sum + (traitMaxValues[child.id] || 0),
        0,
      );
    });

return totals;
  }, [traitGroups, traitMaxValues]);

  const totalMaxPoints = Object.values(parentTotals).reduce(
    (sum, val) => sum + (Number.isFinite(val) ? val : 0),
    0,
  );

  const displayGrades = modelGrades || savedModelGrades;

  const gradeRanges = useMemo((): GradeRange[] => {
    if (!displayGrades || displayGrades.length === 0 || totalMaxPoints <= 0) {
      return [];
    }

    const gradeCount = displayGrades.length;
    const step = Math.max(Math.floor(totalMaxPoints / gradeCount), 1);

    return displayGrades.map((grade, idx) => {
      const start = idx * step;
      const end =
        idx === gradeCount - 1
          ? totalMaxPoints
          : Math.max(start, (idx + 1) * step - 1);

      return { id: grade.id, start, end };
    });
  }, [displayGrades, totalMaxPoints]);

  const personnelSource =
    personnelData && personnelData.length > 0 ? personnelData : employeeData;

  const uniqueJobSeniorities = useMemo((): JobSeniorityCombination[] => {
    const combos = new Set<string>();

    personnelSource.forEach((emp) => {
      const job = emp.function || t("paygap.gradeEstimator.defaultJobTitle");
      const seniority = String(
        emp.professionalPosition ?? t("paygap.gradeEstimator.defaultSeniority"),
      );

      combos.add(`${job}|||${seniority}`);
    });

    return Array.from(combos)
      .map((key) => {
        const [jobTitle, seniority] = key.split("|||");

        return { jobTitle, seniority, key };
      })
      .sort(
        (a, b) =>
          a.jobTitle.localeCompare(b.jobTitle) ||
          a.seniority.localeCompare(b.seniority),
      );
  }, [personnelSource, t]);

  // Handlers
  const getGradeForPoints = useCallback(
    (sum: number): string => {
      if (!displayGrades || displayGrades.length === 0 || totalMaxPoints <= 0) {
        return "-";
      }

      const rangeMatch =
        gradeRanges.find((r) => sum >= r.start && sum <= r.end) ||
        gradeRanges[gradeRanges.length - 1];
      const idx =
        gradeRanges.findIndex((r) => r.id === rangeMatch?.id) >= 0
          ? gradeRanges.findIndex((r) => r.id === rangeMatch?.id)
          : displayGrades.length - 1;
      const grade = displayGrades[idx];

      return String(grade?.id ?? idx + 1);
    },
    [displayGrades, gradeRanges, totalMaxPoints],
  );

  const buildComboGradeMap = useCallback((): Record<string, number> => {
    const parentKeys = ["1", "2", "3", "4"];
    const map: Record<string, number> = {};

    uniqueJobSeniorities.forEach((combo) => {
      // First check if there's a matched grade from position list selection
      const matchedGrade = matchedGrades[combo.key];

      if (matchedGrade != null) {
        map[combo.key] = matchedGrade;

        return;
      }

      // Otherwise calculate from points
      const current: Record<string, number> = comboPoints[combo.key] || {};
      const sum = parentKeys.reduce(
        (s, parentId) => s + (current[parentId] || 0),
        0,
      );
      const gradeStr = getGradeForPoints(sum);
      const gradeNum = Number(gradeStr);

      if (!Number.isNaN(gradeNum)) {
        map[combo.key] = gradeNum;
      }
    });

    return map;
  }, [uniqueJobSeniorities, comboPoints, getGradeForPoints, matchedGrades]);

  const applyGradesToEmployees = async (gradingLevel: GradingLevelType): Promise<void> => {
    const gradeMap = buildComboGradeMap();

    if (Object.keys(gradeMap).length === 0) {
      setShowGradeOverwriteConfirm(false);

return;
    }

    // Use personnelData from DB if available, otherwise fall back to local employeeData
    const sourceData =
      personnelData && personnelData.length > 0 ? personnelData : employeeData;

    if (sourceData.length === 0) {
      console.warn("[applyGradesToEmployees] No employee data available");
      setShowGradeOverwriteConfirm(false);

return;
    }

    const updated: EmployeeData[] = sourceData.map((emp) => {
      const job = emp.function || "";
      const seniority = String(emp.professionalPosition ?? "");
      const key = `${job}|||${seniority}`;
      const grade = gradeMap[key];

      if (grade === undefined) return emp as EmployeeData;

      // Update the specified grading level field
      return {
        ...emp,
        [gradingLevel]: grade,
      } as EmployeeData;
    });

    // Update UI state
    setEmployeeData(updated);

    // Save to database
    await handleImportData(updated);

    setShowGradeOverwriteConfirm(false);
  };

  const runGradeEstimation = async (): Promise<void> => {
    if (Object.keys(comboPoints).length === 0 || !user?.activeCompanyId) {
      return;
    }
    try {
      const result = await estimateGrades({
        companyId: user.activeCompanyId,
        traitMaxValues,
        comboPoints,
      });

      setModelGrades(result);
    } catch {
      // Error is handled by useEstimateGrades hook
    }
  };

  // Build the full settings payload to prevent data loss on query refetch.
  // Every save triggers a query invalidation → refetch → useEffect resets all state.
  // By always sending the complete state, the server stays in sync with local state.
  const buildFullSettings = useCallback(
    (
      overrides: Record<string, unknown> = {},
    ): Record<string, unknown> => ({
      comboPoints,
      traitMaxValues,
      questionnaireAnswers,
      positionList,
      jobTitleMatches,
      matchedGrades,
      gradeEstimatorResults: modelGrades || savedModelGrades,
      ...overrides,
    }),
    [
      comboPoints,
      traitMaxValues,
      questionnaireAnswers,
      positionList,
      jobTitleMatches,
      matchedGrades,
      modelGrades,
      savedModelGrades,
    ],
  );

  const handleSaveGrades = (): void => {
    const gradesToSave = modelGrades || savedModelGrades;

    if (!gradesToSave || gradesToSave.length === 0) {
      // Still show success message even if nothing to save
      setShowSaveSuccess(true);
      setTimeout(() => setShowSaveSuccess(false), 3000);

      return;
    }

    // Save to database via API
    updatePaygapSettings(
      buildFullSettings({ gradeEstimatorResults: gradesToSave }),
    );
    setSavedModelGrades(gradesToSave);
    setShowSaveSuccess(true);
    setTimeout(() => setShowSaveSuccess(false), 3000);
  };

  // Explicit save handler for all settings (comboPoints, traitMaxValues, questionnaireAnswers)
  const handleSaveSettings = useCallback((): void => {
    updatePaygapSettings(buildFullSettings());
  }, [buildFullSettings, updatePaygapSettings]);

  // Save questionnaire answers and computed comboPoints for a specific combo (called when Apply button is clicked)
  const handleSaveQuestionnaireAnswers = useCallback(
    (
      comboKey: string,
      newAnswers: PositionAnswersMap,
      updatedComboPoints: Record<string, Record<string, number>>,
    ): void => {
      const updatedQuestionnaireAnswers = {
        ...questionnaireAnswers,
        [comboKey]: newAnswers,
      };

      updatePaygapSettings(
        buildFullSettings({
          questionnaireAnswers: updatedQuestionnaireAnswers,
          comboPoints: updatedComboPoints,
        }),
      );
    },
    [questionnaireAnswers, buildFullSettings, updatePaygapSettings],
  );

  // Save position list to the database
  const handleSavePositionList = useCallback(
    (list: PositionListItem[]): void => {
      updatePaygapSettings(buildFullSettings({ positionList: list }));
    },
    [buildFullSettings, updatePaygapSettings],
  );

  // Save jobTitleMatches and matchedGrades to the database
  const handleSaveJobTitleMatches = useCallback(
    (
      updatedJobTitleMatches: Record<string, string | null>,
      updatedMatchedGrades: Record<string, number | null>,
    ): void => {
      updatePaygapSettings(
        buildFullSettings({
          jobTitleMatches: updatedJobTitleMatches,
          matchedGrades: updatedMatchedGrades,
        }),
      );
    },
    [buildFullSettings, updatePaygapSettings],
  );

  // Sync positions: merge matched job titles into position list
  const handleSyncPositions = useCallback((): void => {
    const parentKeys = ["1", "2", "3", "4"];
    const updatedPositionList = [...positionList];

    // Find the next available numeric ID
    const getNextId = (): string => {
      const numericIds = updatedPositionList
        .map((p) => parseInt(p.id, 10))
        .filter((n) => !Number.isNaN(n));
      const maxId = numericIds.length > 0 ? Math.max(...numericIds) : 0;


return String(maxId + 1);
    };

    uniqueJobSeniorities.forEach((combo) => {
      const matchedPositionId = jobTitleMatches[combo.key];

      if (matchedPositionId) {
        // Found a match: append the job title to similarJobTitles
        const positionIndex = updatedPositionList.findIndex(
          (p) => p.id === matchedPositionId,
        );

        if (positionIndex >= 0) {
          const position = updatedPositionList[positionIndex];
          const existingSimilar = position.similarJobTitles
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean);

          // Only add if not already present
          if (
            !existingSimilar.includes(combo.jobTitle) &&
            position.jobTitle !== combo.jobTitle
          ) {
            existingSimilar.push(combo.jobTitle);
            updatedPositionList[positionIndex] = {
              ...position,
              similarJobTitles: existingSimilar.join(", "),
            };
          }
        }
      } else {
        // No match: check if we have a valid grade to add
        // First check if there's a matched grade from dropdown selection
        const matchedGrade = matchedGrades[combo.key];

        let gradeNum: number | null = null;

        if (matchedGrade != null) {
          // Use the grade from the matched position (dropdown selection)
          gradeNum = matchedGrade;
        } else {
          // Calculate grade from points
          const current: Record<string, number> = comboPoints[combo.key] || {};
          const sum = parentKeys.reduce(
            (s, parentId) => s + (current[parentId] || 0),
            0,
          );
          const gradeStr = getGradeForPoints(sum);
          // Only use calculated grade if it's valid (not "-")

          if (gradeStr !== "-") {
            const parsedGrade = Number(gradeStr);

            if (!Number.isNaN(parsedGrade) && parsedGrade > 0) {
              gradeNum = parsedGrade;
            }
          }
        }

        // Only add if we have a valid grade and position doesn't already exist
        if (gradeNum !== null) {
          const alreadyExists = updatedPositionList.some(
            (p) =>
              p.jobTitle === combo.jobTitle ||
              p.similarJobTitles
                .split(",")
                .map((s) => s.trim())
                .includes(combo.jobTitle),
          );

          if (!alreadyExists) {
            updatedPositionList.push({
              id: getNextId(),
              jobTitle: combo.jobTitle,
              similarJobTitles: "",
              grade: gradeNum,
            });
          }
        }
      }
    });

    // Update state and save to database
    setPositionList(updatedPositionList);
    updatePaygapSettings({
      positionList: updatedPositionList,
    });
  }, [
    positionList,
    uniqueJobSeniorities,
    jobTitleMatches,
    matchedGrades,
    comboPoints,
    getGradeForPoints,
    updatePaygapSettings,
  ]);

  // Effects
  // Load savedModelGrades, comboPoints, traitMaxValues, and questionnaireAnswers from API (database)
  useEffect(() => {
    if (!paygapSettings) return;

    // Load grade estimator results
    if (paygapSettings.gradeEstimatorResults) {
      setSavedModelGrades(
        paygapSettings.gradeEstimatorResults as GradeEstimatorResponseDto[],
      );
    } else {
      setSavedModelGrades(null);
    }

    // Load comboPoints
    if (paygapSettings.comboPoints) {
      setComboPoints(
        paygapSettings.comboPoints as Record<string, Record<string, number>>,
      );
    } else {
      setComboPoints({});
    }

    // Load traitMaxValues
    if (paygapSettings.traitMaxValues) {
      setTraitMaxValues(paygapSettings.traitMaxValues as Record<string, number>);
    }

    // Load questionnaireAnswers
    if (paygapSettings.questionnaireAnswers) {
      setQuestionnaireAnswers(
        paygapSettings.questionnaireAnswers as Record<string, PositionAnswersMap>,
      );
    } else {
      setQuestionnaireAnswers({});
    }

    // Load positionList
    if (paygapSettings.positionList) {
      setPositionList(paygapSettings.positionList as PositionListItem[]);
    } else {
      setPositionList([]);
    }

    // Load jobTitleMatches
    if (paygapSettings.jobTitleMatches) {
      setJobTitleMatches(
        paygapSettings.jobTitleMatches as Record<string, string | null>,
      );
    } else {
      setJobTitleMatches({});
    }

    // Load matchedGrades
    if (paygapSettings.matchedGrades) {
      setMatchedGrades(
        paygapSettings.matchedGrades as Record<string, number | null>,
      );
    } else {
      setMatchedGrades({});
    }
  }, [paygapSettings]);

  // Save modelGrades to database when new grades are generated
  useEffect(() => {
    if (!modelGrades || !user?.activeCompanyId) return;

    // Save to database via API
    updatePaygapSettings({
      gradeEstimatorResults: modelGrades,
    });
    setSavedModelGrades(modelGrades);
  }, [modelGrades, user?.activeCompanyId, updatePaygapSettings]);

  // Refetch personnel data when opening points tab
  useEffect(() => {
    if (showGradeEstimator) {
      refetchGetCurrentCompanyPersonnel();
    }
  }, [showGradeEstimator, refetchGetCurrentCompanyPersonnel]);

  // Check if grade estimation can be run (requires job/seniority points)
  const canRunEstimation = Object.keys(comboPoints).length > 0;

  return {
    showGradeEstimator,
    setShowGradeEstimator,
    showGradeOverwriteConfirm,
    setShowGradeOverwriteConfirm,
    showSaveSuccess,
    traitMaxValues,
    setTraitMaxValues,
    comboPoints,
    setComboPoints,
    questionnaireAnswers,
    setQuestionnaireAnswers,
    selectedGradingLevel,
    setSelectedGradingLevel,
    positionList,
    setPositionList,
    jobTitleMatches,
    setJobTitleMatches,
    matchedGrades,
    setMatchedGrades,
    parentTotals,
    totalMaxPoints,
    displayGrades,
    gradeRanges,
    uniqueJobSeniorities,
    isLoadingGetCurrentCompanyPersonnel,
    isModelLoading,
    modelError,
    runGradeEstimation,
    resetModelState,
    handleSaveGrades,
    handleSaveSettings,
    handleSaveQuestionnaireAnswers,
    applyGradesToEmployees,
    getGradeForPoints,
    handleSavePositionList,
    handleSaveJobTitleMatches,
    handleSyncPositions,
    canRunEstimation,
  };
};
